// Turning what a student typed into something comparable with the answer key.
//
// This lives in one place on purpose. It used to be copy-pasted into ModuleQuiz,
// QuizComplete and QuestionRenderer, which meant the same answer could in principle be
// marked one way in the module quiz and another on the practice results page.

/**
 * LaTeX and plain text into one comparable form.
 *
 * MathLive gives us LaTeX (\frac{1}{2}, x^{2}); the answer key is stored as plain text
 * (1/2, x^2). Both sides are reduced to the same shape before comparing.
 */
export const normalizeMath = (raw) => {
  if (raw == null) return "";
  let s = String(raw).trim();

  s = s.replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, "($1)/($2)");
  s = s.replace(/(\^|_)\{([^{}]+)\}/g, "$1$2");
  s = s.replace(/\{([^{}]*)\}/g, "$1");
  s = s
    .replace(/\\cdot|\\times/g, "*")
    .replace(/\\div/g, "/")
    .replace(/\\pi/g, "pi")
    .replace(/\\sqrt\[([^\]]+)\]/g, "root$1")
    .replace(/\\sqrt/g, "sqrt")
    .replace(/\\pm/g, "±")
    .replace(/\\infty/g, "∞")
    .replace(/\\[a-zA-Z]+/g, "");

  s = s.replace(/\s+/g, "");
  return s.toLowerCase();
};

/**
 * A numeric value for an answer, or NaN when it is not a number at all.
 *
 * The fraction form is tested BEFORE parseFloat, and that order is the whole point:
 * parseFloat("3/4") stops at the slash and returns 3, so a fraction was being scored as
 * its own numerator. A student answering 3/4 to a question keyed 0.75 was marked wrong,
 * and tolerance comparisons were meaningless (|3 - 0.75| = 2.25).
 */
export const evalNumber = (value) => {
  const text = String(value ?? "").trim();

  const fraction = text.match(/^\(?([-\d.]+)\)?\/\(?([-\d.]+)\)?$/);
  if (fraction) {
    const denominator = parseFloat(fraction[2]);
    if (!denominator) return NaN;

    return parseFloat(fraction[1]) / denominator;
  }

  const direct = parseFloat(text);
  return isNaN(direct) ? NaN : direct;
};

/**
 * Fisher-Yates. `sort(() => Math.random() - 0.5)` is not a shuffle — it leaves the
 * second option in position 2 or 3 about 76% of the time, which lets a student learn
 * where the answer tends to sit instead of learning the subject.
 */
export const shuffleArray = (arr) => {
  const copy = [...(arr ?? [])];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};
